import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, Eye, FileText, Paintbrush, Truck } from "lucide-react";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";

const steps = [
  {
    icon: ClipboardList,
    title: "1. Tell us your idea",
    body: "Customer submits a quote request with wording, size, colors, theme, and any inspiration images.",
  },
  {
    icon: Eye,
    title: "2. We review the design",
    body: "We check the idea for laser-cutting, layering, sizing, and structure.",
  },
  {
    icon: FileText,
    title: "3. We send a quote",
    body: "We confirm the price, timeline, and design direction before starting.",
  },
  {
    icon: ArrowRight,
    title: "4. Design and approval",
    body: "We create or prepare the design and send a preview when needed.",
  },
  {
    icon: Paintbrush,
    title: "5. Cut, paint, and assemble",
    body: "Your piece is laser-cut from basswood, finished with paint or stain, and assembled by hand.",
  },
  {
    icon: Truck,
    title: "6. Pickup, delivery, or shipping",
    body: "We arrange the best way to get the finished piece to you.",
  },
];

export default function HowItWorks() {
  return (
    <div className="min-h-screen" data-testid="process-page">
      <Seo
        title="How It Works"
        description="Learn how Beyond Board Woodworking takes a custom idea from design and approval through laser cutting, finishing, and completion."
        path="/process"
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-12">
        <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B] mb-3">How It Works</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F0E6] leading-tight">A clear custom-order process from quote request to finished basswood piece</h1>
        <p className="mt-6 text-[#B8B7B2] max-w-3xl text-lg leading-relaxed">
          Whether you have a polished idea or just a rough direction, our process is built to
          help you move from inspiration to a handcrafted final piece with confidence.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {steps.map(({ icon: Icon, title, body }, i) => (
          <div
            key={title}
            className="process-step-panel p-8 rounded-2xl transition fade-up"
            style={{ animationDelay: `${i * 80}ms` }}
            data-testid={`process-step-${i + 1}`}
          >
            <div className="w-12 h-12 rounded-md bg-[#D8A34B]/15 border border-[#D8A34B]/40 flex items-center justify-center mb-5">
              <Icon className="w-5 h-5 text-[#D8A34B]" />
            </div>
            <h3 className="font-display text-2xl text-[#F5F0E6]">{title}</h3>
            <p className="text-[#B8B7B2] text-sm mt-3 leading-relaxed">{body}</p>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="rounded-3xl overflow-hidden border border-stone-100/10 grid grid-cols-1 lg:grid-cols-2">
          <div className="process-notes-panel p-10 lg:p-16">
            <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B] mb-3">Custom Order Notes</p>
            <h2 className="font-display text-3xl text-[#F5F0E6]">Some projects need more design prep before production</h2>
            <p className="mt-5 text-[#B8B7B2] leading-relaxed">
              Complex designs, logos, portraits, sports themes, memorial pieces, and
              multi-layer signs may require extra design time before production. If you are unsure
              how to explain your idea, send what you have and we will review the best direction.
            </p>
            <Link to="/quote">
              <Button className="process-natural-button mt-7 h-12 px-6">
                Request a Custom Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="aspect-[4/3] lg:aspect-auto">
            <img src="https://images.unsplash.com/photo-1597960194599-22929afc25b1?w=1400&auto=format&fit=crop" alt="woodworking studio" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
